import React, {useCallback, useEffect, useRef, useState} from 'react';

import EditableTextField from 'ui-library/inputs/EditableTextField';

const ANALYSIS_TEMPLATE_NAME_MAX_LENGTH = 255;

interface AnalysisTemplateNameEditorProps {
  name: string;
  onRename: (newValue: string | number) => Promise<void>;
  disabled?: boolean;
}

export default function AnalysisTemplateNameEditor(props: AnalysisTemplateNameEditorProps): JSX.Element {
  const {onRename: onRenameHandler} = props;
  const [name, setName] = useState(props.name);
  const [isDisabled, setIsDisabled] = useState(props.disabled ?? false);
  const displayName = isDisabled ? name : props.name;
  const mountedRef = useRef(true);

  /**
   * While the onRename callback is running (and probably invoking the API), we use a placeholder value.
   * Otherwise, the name that comes on props will be shown briefly.
   */
  const onRename = useCallback(
    async (newValue: string | number): Promise<void> => {
      setIsDisabled(true);
      setName(String(newValue));
      await onRenameHandler(newValue);

      // Do not try to set state if the component is unmounted. This can happen if while the async
      // onRenameHandler is running, the user navigates away.
      if (!mountedRef.current) {
        return;
      }

      setIsDisabled(false);
    },
    [onRenameHandler]
  );

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  return (
    <EditableTextField
      dataTestAttrs={{component: 'AnalysisTemplateNameEditor', element: 'title'}}
      isDisabled={isDisabled}
      maxLength={ANALYSIS_TEMPLATE_NAME_MAX_LENGTH}
      requestChange={onRename}
      value={displayName}
      viewerClassName="header--header1"
    />
  );
}
